import React, { useMemo, useState } from "react";
import {
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    IconButton,
    Input,
    LoadingProgress,
    Modal,
    Panel,
    Switch,
    Table,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { useMutation } from "@apollo/react-hooks";
import {
    MUTATION_UPDATE_ALPINEIQ,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import {
    useFormActions,
    useForm,
    useMeta,
    useNotification,
    useValidator,
} from "@/hooks";
import FavoriteStoreAlpineIOForm from "./FavoriteStoresAlpineIOForm";
import { VARIABLES_TEMP_DEFAULT } from "config/constant";
import { PlusIcon, RowActionsMenu, storeTableUiStyles } from "./shared";
import styles from "./GeneralsForm.module.scss";
import alpineStyles from "./AlpineIOForm.module.scss";
import deliveryStyles from "./DeliverySchedules.module.scss";

const SaveIcon = () => <Icon icon="save" />;
const CopyIcon = () => <Icon icon="copy" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const AlpineIOForm = ({ app, loading: pageLoading }) => {
    const notify = useNotification();
    const { user } = useMeta();
    const [showForm, setShowForm] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [rowSelect, setRowSelect] = useState(null);
    const [favorites_stores, setFavorites_Stores] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const { inputs, setInputs, inputChange } = useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "user_id",
                method: "isEmpty",
                validWhen: false,
                message: "User Id is required.",
            },
            {
                field: "api_key",
                method: "isEmpty",
                validWhen: false,
                message: "Api Key is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
        setFavorites_Stores(app.favorites_stores);
    }

    const options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                favorites_stores,
                type: "AlpineIQ",
            },
        },
    };

    const [mutate] = useMutation(MUTATION_UPDATE_ALPINEIQ, {
        refetchQueries: [],
        onCompleted: () => {
            handleCancel();
            setIsUpdating(false);
            setIsDelete(false);
            setShowDeleteConfirm(false);
            setInputs({
                ...inputs,
                favorites_stores,
            });
            notify(
                `Favorite Store Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully"`,
                "success",
            );
        },
        onError: () => {
            setIsUpdating(false);
            notify(
                `Error ${
                    isCreate ? "Updating" : !isDelete ? "Creating" : "Removing"
                }  The Favorite Store, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "AlpineIQ",
        "",
        MUTATION_UPDATE_ALPINEIQ,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
                skip: user.loading || !user.accountId || !user.storeId,
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    const rawStores = (inputs && inputs.favorites_stores) || [];
    const rows = useMemo(
        () =>
            rawStores.map((store, index) => ({
                ...store,
                _index: index,
            })),
        [rawStores],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm}
                            label={row.id || `store ${row._index + 1}`}
                            onDelete={() => {
                                setIsDelete(true);
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowDeleteConfirm(true);
                            }}
                            onUpdate={() => {
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowForm(true);
                            }}
                        />
                    </span>
                ),
            },
            {
                id: "id",
                header: "Store Id",
                minWidth: 180,
                renderCell: row =>
                    row.id ? <Badge variant="primary">{row.id}</Badge> : "",
            },
            {
                id: "name",
                header: "Store Name",
                minWidth: 220,
                renderCell: row => row.name || "",
            },
        ],
        [isUpdating, showForm],
    );

    const handlerUpdatingOrCreate = array => {
        if (!isValid()) {
            return;
        }

        setIsUpdating(true);

        setFavorites_Stores(array);
        options.variables.input.favorites_stores = array;
        mutate(options);
    };

    const handleCancel = () => {
        setIsUpdating(false);
        setShowForm(false);
    };

    const confirmDelete = () => {
        const nextStores = _.filter(
            inputs.favorites_stores,
            (store, index) => index !== rowSelect,
        );
        setFavorites_Stores(nextStores);
        setIsUpdating(true);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...inputs,
                    favorites_stores: nextStores,
                    type: "AlpineIQ",
                },
            },
        });
    };

    const toggleModal = () => {
        setIsOpen(!isOpen);
    };

    const clipboardCopy = text => {
        navigator.clipboard.writeText(text);
        notify(`${text} is copied`, "success");
    };

    return (
        <>
            <Modal
                description="Are You Sure You Want To Remove The Favorite Store ?"
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setIsDelete(false);
                            }}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) {
                        setShowDeleteConfirm(false);
                        setIsDelete(false);
                    }
                }}
                open={showDeleteConfirm}
                title="Remove favorite store"
            >
                {null}
            </Modal>

            <Modal
                className={alpineStyles.variablesModal}
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button onClick={toggleModal} variant="secondary">
                            Close
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) setIsOpen(false);
                }}
                open={isOpen}
                size="lg"
                title="Available variables"
            >
                <div className={alpineStyles.variablesBody}>
                    {VARIABLES_TEMP_DEFAULT.map((item, index) => (
                        <div className={alpineStyles.variableGroup} key={index}>
                            <Heading
                                className={alpineStyles.variableGroupTitle}
                                level={5}
                            >
                                {item.label}
                            </Heading>
                            <ul className={alpineStyles.variableList}>
                                {item.value.map((subItem, subIndex) => (
                                    <li
                                        className={alpineStyles.variableRow}
                                        key={subIndex}
                                    >
                                        <span
                                            className={
                                                alpineStyles.variableToken
                                            }
                                        >
                                            {subItem}
                                        </span>
                                        <IconButton
                                            aria-label={`Copy ${subItem}`}
                                            className={
                                                alpineStyles.variableCopy
                                            }
                                            onClick={() =>
                                                clipboardCopy(subItem)
                                            }
                                            size="sm"
                                            type="button"
                                            variant="tertiary"
                                        >
                                            <CopyIcon />
                                        </IconButton>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>AlpineIQ</Heading>
                            <Text tone="secondary" variant="body-sm">
                                AlpineIQ Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "update",
                                    label: "Update",
                                    icon: loading ? undefined : <SaveIcon />,
                                    loading,
                                    disabled: loading || isUpdating,
                                    onClick: () => createOrUpdate(),
                                },
                                {
                                    id: "add-store",
                                    label: "Add Store",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: isUpdating || showForm,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowForm(true);
                                    },
                                },
                            ]}
                            aria-label="AlpineIQ actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {user.loading || pageLoading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <FormLayout>
                        <FormLayout.Section title="Information">
                            <Switch
                                checked={Boolean(inputs.used_for_subscription)}
                                label="Used for subscription"
                                name="used_for_subscription"
                                onChange={inputChange}
                            />
                            <Switch
                                checked={Boolean(inputs.used_for_rewards)}
                                label="Used for rewards"
                                name="used_for_rewards"
                                onChange={inputChange}
                            />
                            <FormLayout.Grid columns={2} maxWidth="twoThirds">
                                <Input
                                    fullWidth
                                    label="Campaign Id"
                                    name="campaign_id"
                                    onChange={inputChange}
                                    placeholder="Campaign id"
                                    value={inputs.campaign_id || ""}
                                />
                            </FormLayout.Grid>
                            <FormLayout.Grid columns={2} maxWidth="twoThirds">
                                <Input
                                    error={fieldError(validator, "user_id")}
                                    fullWidth
                                    label="User Id"
                                    name="user_id"
                                    onChange={inputChange}
                                    placeholder="User id"
                                    value={inputs.user_id || ""}
                                />
                                <Input
                                    error={fieldError(validator, "api_key")}
                                    fullWidth
                                    label="Api Key"
                                    name="api_key"
                                    onChange={inputChange}
                                    placeholder="Api key"
                                    value={inputs.api_key || ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Transactional SMS">
                            <div className={alpineStyles.smsHeader}>
                                <Switch
                                    checked={Boolean(inputs.notify_by_sms)}
                                    label="Notify by SMS"
                                    name="notify_by_sms"
                                    onChange={inputChange}
                                />
                                <Button
                                    disabled={isOpen}
                                    onClick={toggleModal}
                                    type="button"
                                    variant="secondary"
                                >
                                    View Variables
                                </Button>
                            </div>
                            {inputs.notify_by_sms ? (
                                <FormLayout.Grid columns={2}>
                                    <Switch
                                        checked={Boolean(
                                            inputs.order_confirm_sms_message_active,
                                        )}
                                        label="Activate Order Confirm SMS"
                                        name="order_confirm_sms_message_active"
                                        onChange={inputChange}
                                    />
                                    <Switch
                                        checked={Boolean(
                                            inputs.order_ready_sms_message_active,
                                        )}
                                        label="Activate Order Ready SMS"
                                        name="order_ready_sms_message_active"
                                        onChange={inputChange}
                                    />
                                    <Switch
                                        checked={Boolean(
                                            inputs.order_canceled_sms_message_active,
                                        )}
                                        label="Activate Order Canceled SMS"
                                        name="order_canceled_sms_message_active"
                                        onChange={inputChange}
                                    />
                                    <Switch
                                        checked={Boolean(
                                            inputs.order_completed_sms_message_active,
                                        )}
                                        label="Activate Order Completed SMS"
                                        name="order_completed_sms_message_active"
                                        onChange={inputChange}
                                    />
                                </FormLayout.Grid>
                            ) : null}
                        </FormLayout.Section>

                        {showForm ? (
                            <FavoriteStoreAlpineIOForm
                                app={inputs}
                                handleCancel={handleCancel}
                                handlerUpdatingOrCreate={
                                    handlerUpdatingOrCreate
                                }
                                isCreate={isCreate}
                                isUpdating={isUpdating}
                                selectStore={rowSelect}
                                setStore={setFavorites_Stores}
                            />
                        ) : null}

                        <FormLayout.Section title="Favorites Stores">
                            <Table
                                columns={columns}
                                getRowId={row => String(row._index)}
                                rows={rows}
                            />
                        </FormLayout.Section>
                    </FormLayout>
                )}
            </Panel>
        </>
    );
};

export default AlpineIOForm;
